import { Helmet } from "react-helmet-async";
import { useParams, Link } from "react-router-dom";
import { getBrandBySlug, isProduction } from "@/lib/comparatifs";

export default function ComparatifsAlternatives() {
  const { slug } = useParams<{ slug: string }>();
  const brand = getBrandBySlug(slug || "");

  if (!brand) {
    return <div>Marque non trouvée</div>;
  }

  const canonicalUrl = `https://www.cosmos-wave.com/comparatifs/alternatives/${brand.slug}`;
  const title = `Alternatives à ${brand.name} (2025) — comparatif neutre`;
  const description = `Alternatives à ${brand.name} : comparaison neutre avec Cosmos Wave, agences spécialisées et freelances. Critères, méthodes, pays, budgets pour votre choix Amazon.`;

  const faqItems = [
    {
      question: `Pourquoi chercher une alternative à ${brand.name} ?`,
      answer: `Selon votre secteur, budget ou besoins spécifiques (IA, SP-API, expansion EU), d'autres profils peuvent mieux correspondre à vos objectifs Amazon.`
    },
    {
      question: "Comment évaluer ces alternatives objectively ?",
      answer: "Comparez les méthodes (data-driven vs intuition), stack technique (IA/SP-API), zones géographiques, références sectorielles et transparence tarifaire."
    },
    {
      question: "Quel budget prévoir pour une agence Amazon ?",
      answer: "De 2-5K€/mois (freelance spécialisé) à 15-50K€/mois (agence full-service), selon le CA Amazon, la complexité catalogue et les pays ciblés."
    }
  ];

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": `Alternatives à ${brand.name}`,
    "description": description,
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Cosmos Wave",
        "description": "Agence Amazon & IA avec méthode data-driven, stack SP-API et spécialisation Buy Box/Catalog Health."
      },
      {
        "@type": "ListItem", 
        "position": 2,
        "name": "Profil 360°",
        "description": "Agence généraliste avec équipes Ads, SEO, Creative et expansion européenne."
      },
      {
        "@type": "ListItem",
        "position": 3, 
        "name": "Boutique IA/Ops",
        "description": "Spécialiste automation, SP-API et outils propriétaires pour optimisation opérationnelle."
      },
      {
        "@type": "ListItem",
        "position": 4,
        "name": "Freelance/Collectif",
        "description": "Expert indépendant ou collectif spécialisé pour budgets intermédiaires et approche personnalisée."
      }
    ]
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Accueil",
        "item": "https://www.cosmos-wave.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Comparatifs",
        "item": "https://www.cosmos-wave.com/comparatifs"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": `Alternatives à ${brand.name}`,
        "item": canonicalUrl
      }
    ]
  };

  const professionalServiceSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "Cosmos Wave",
    "description": "Agence Amazon & IA spécialisée en Ads SP/SB/SD/DSP, SEO listings & A+, Catalog Health et expansion européenne.",
    "provider": {
      "@type": "Organization",
      "name": "Cosmos Wave",
      "url": "https://www.cosmos-wave.com",
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "sales",
        "email": "contact@cosmos-wave.com",
        "areaServed": "FR"
      }
    },
    "areaServed": {
      "@type": "Country", 
      "name": "France"
    },
    "serviceType": "Amazon Marketing Services"
  };

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        {!isProduction() && <meta name="robots" content="noindex,nofollow" />}
        
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Cosmos Wave" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content="https://www.cosmos-wave.com/og/comparatifs-alternatives-1200x630.jpg" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content="https://www.cosmos-wave.com/og/comparatifs-alternatives-1200x630.jpg" />
        
        <script type="application/ld+json">{JSON.stringify(itemListSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(professionalServiceSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-background">
        
        
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            <header className="mb-16">
              <h1 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
                Alternatives à <span className="text-primary">{brand.name}</span>
                <span className="block text-2xl md:text-3xl mt-4 text-muted-foreground">
                  Comparatif neutre (2025)
                </span>
              </h1>
              <p className="text-xl text-muted-foreground">
                Comparaison objective des alternatives selon vos besoins Amazon : budget, spécialisation, méthodes et zones géographiques.
              </p>
            </header>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-6 text-foreground">
                Pour qui {brand.name} peut convenir
              </h2>
              <div className="bg-card rounded-lg p-6 border border-border">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold text-foreground mb-3">Spécialisations</h3>
                    <div className="space-y-2">
                      {brand.focus.map((focus) => (
                        <span key={focus} className="inline-block mr-2 mb-2 px-3 py-1 rounded-full text-sm bg-primary/10 text-primary">
                          {focus}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-3">Zones</h3>
                    <div className="space-y-2">
                      {brand.regions.map((region) => (
                        <span key={region} className="inline-block mr-2 mb-2 px-3 py-1 rounded-full text-sm bg-secondary/10 text-secondary-foreground">
                          {region}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-6 text-foreground">
                Quand chercher une alternative
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-card rounded-lg p-6 border border-border">
                  <h3 className="font-semibold text-foreground mb-3">Besoins spécifiques</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• Stack IA et automation SP-API</li>
                    <li>• Focus Buy Box et Catalog Health</li>
                    <li>• Méthode 100% data-driven</li>
                    <li>• Expansion EU avec conformité DMA</li>
                  </ul>
                </div>
                <div className="bg-card rounded-lg p-6 border border-border">
                  <h3 className="font-semibold text-foreground mb-3">Budget et approche</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• Budget optimisé selon TACOS</li>
                    <li>• Transparence méthodologique</li>
                    <li>• Livraisons rapides (15-30j)</li>
                    <li>• Accompagnement formation</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-6 text-foreground">
                4 Alternatives recommandées
              </h2>
              <div className="space-y-6">
                <div className="bg-primary/5 rounded-lg p-6 border border-primary/20">
                  <h3 className="text-xl font-semibold text-foreground mb-3">
                    1. Cosmos Wave (IA & SP-API)
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Agence Amazon & IA avec méthode data-driven, stack SP-API et spécialisation Buy Box/Catalog Health. 
                    Audit gratuit 48-72h, Sprint Buy Box 15j, Agent IA 30j.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 rounded-full text-sm bg-primary text-primary-foreground">IA & Automation</span>
                    <span className="px-3 py-1 rounded-full text-sm bg-primary text-primary-foreground">SP-API</span>
                    <span className="px-3 py-1 rounded-full text-sm bg-primary text-primary-foreground">Buy Box</span>
                  </div>
                </div>

                <div className="bg-card rounded-lg p-6 border border-border">
                  <h3 className="text-xl font-semibold text-foreground mb-3">
                    2. Agence 360° généraliste
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Agence full-service avec équipes Ads, SEO, Creative et expansion EU. 
                    Idéale pour marques établies cherchant un partenaire unique multi-pays.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 rounded-full text-sm bg-secondary text-secondary-foreground">Full-service</span>
                    <span className="px-3 py-1 rounded-full text-sm bg-secondary text-secondary-foreground">Multi-pays</span>
                    <span className="px-3 py-1 rounded-full text-sm bg-secondary text-secondary-foreground">Équipes dédiées</span>
                  </div>
                </div>

                <div className="bg-card rounded-lg p-6 border border-border">
                  <h3 className="text-xl font-semibold text-foreground mb-3">
                    3. Boutique IA/Ops spécialisée
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Spécialiste automation, SP-API et outils propriétaires. 
                    Pour marques tech cherchant optimisation opérationnelle avancée.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 rounded-full text-sm bg-accent text-accent-foreground">Automation</span>
                    <span className="px-3 py-1 rounded-full text-sm bg-accent text-accent-foreground">Outils propriétaires</span>
                    <span className="px-3 py-1 rounded-full text-sm bg-accent text-accent-foreground">Tech-focus</span>
                  </div>
                </div>

                <div className="bg-card rounded-lg p-6 border border-border">
                  <h3 className="text-xl font-semibold text-foreground mb-3">
                    4. Freelance/Collectif expert
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Expert indépendant ou collectif spécialisé. 
                    Solution budget-optimisée avec approche personnalisée et réactivité maximale.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 rounded-full text-sm bg-muted text-muted-foreground">Budget optimisé</span>
                    <span className="px-3 py-1 rounded-full text-sm bg-muted text-muted-foreground">Personnalisé</span>
                    <span className="px-3 py-1 rounded-full text-sm bg-muted text-muted-foreground">Réactif</span>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-6 text-foreground">Tableau comparatif</h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-border rounded-lg">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="border border-border p-3 text-left text-foreground">Critère</th>
                      <th className="border border-border p-3 text-left text-primary">Cosmos Wave</th>
                      <th className="border border-border p-3 text-left text-foreground">Agence 360°</th>
                      <th className="border border-border p-3 text-left text-foreground">Boutique IA/Ops</th>
                      <th className="border border-border p-3 text-left text-foreground">Freelance</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-border p-3 font-medium text-foreground">Amazon Ads</td>
                      <td className="border border-border p-3 text-primary">IA + SP-API</td>
                      <td className="border border-border p-3 text-muted-foreground">Équipe dédiée</td>
                      <td className="border border-border p-3 text-muted-foreground">Automation</td>
                      <td className="border border-border p-3 text-muted-foreground">Expertise</td>
                    </tr>
                    <tr>
                      <td className="border border-border p-3 font-medium text-foreground">SEO & A+</td>
                      <td className="border border-border p-3 text-primary">IA Content Gen</td>
                      <td className="border border-border p-3 text-muted-foreground">Creative Team</td>
                      <td className="border border-border p-3 text-muted-foreground">Tools Focus</td>
                      <td className="border border-border p-3 text-muted-foreground">Spécialisé</td>
                    </tr>
                    <tr>
                      <td className="border border-border p-3 font-medium text-foreground">IA/SP-API</td>
                      <td className="border border-border p-3 text-primary font-semibold">✓ Natif</td>
                      <td className="border border-border p-3 text-muted-foreground">En cours</td>
                      <td className="border border-border p-3 text-muted-foreground">✓ Avancé</td>
                      <td className="border border-border p-3 text-muted-foreground">Variable</td>
                    </tr>
                    <tr>
                      <td className="border border-border p-3 font-medium text-foreground">Ops Catalogue</td>
                      <td className="border border-border p-3 text-primary font-semibold">✓ Buy Box Focus</td>
                      <td className="border border-border p-3 text-muted-foreground">Standard</td>
                      <td className="border border-border p-3 text-muted-foreground">✓ Automation</td>
                      <td className="border border-border p-3 text-muted-foreground">Limité</td>
                    </tr>
                    <tr>
                      <td className="border border-border p-3 font-medium text-foreground">Pays EU</td>
                      <td className="border border-border p-3 text-primary">FR/DE/IT/ES/UK</td>
                      <td className="border border-border p-3 text-muted-foreground">Multi-pays</td>
                      <td className="border border-border p-3 text-muted-foreground">Variable</td>
                      <td className="border border-border p-3 text-muted-foreground">Local</td>
                    </tr>
                    <tr>
                      <td className="border border-border p-3 font-medium text-foreground">Budget mensuel</td>
                      <td className="border border-border p-3 text-primary">5-20K€</td>
                      <td className="border border-border p-3 text-muted-foreground">15-50K€</td>
                      <td className="border border-border p-3 text-muted-foreground">8-25K€</td>
                      <td className="border border-border p-3 text-muted-foreground">2-8K€</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-6 text-foreground">Questions fréquentes</h2>
              <div className="space-y-4">
                {faqItems.map((item, index) => (
                  <div key={index} className="bg-card rounded-lg p-6 border border-border">
                    <h3 className="font-semibold text-foreground mb-3">{item.question}</h3>
                    <p className="text-muted-foreground">{item.answer}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-12 text-center">
              <div className="bg-primary/5 rounded-lg p-8">
                <h2 className="text-2xl font-semibold mb-4 text-foreground">
                  Évaluation gratuite en 48-72h
                </h2>
                <p className="text-muted-foreground mb-6">
                  Diagnostic IA 360° de votre présence Amazon avec comparaison des alternatives adaptées à vos besoins.
                </p>
                <Link 
                  to="/ia/audit" 
                  className="inline-flex items-center justify-center px-6 py-3 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Demander un audit gratuit
                </Link>
              </div>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold mb-4 text-foreground">Liens utiles</h3>
              <div className="flex flex-wrap gap-4 text-sm">
                <Link to="/ia" className="text-primary hover:underline">Solutions IA Amazon</Link>
                <Link to="/services/amazon" className="text-primary hover:underline">Services Amazon</Link>
                <Link to="/ia/agents-assistants-amazon" className="text-primary hover:underline">Agents IA</Link>
                <Link to="/services/buybox-sprint" className="text-primary hover:underline">Sprint Buy Box</Link>
                <Link to="/contact" className="text-primary hover:underline">Contact</Link>
                <Link to="/comparatifs" className="text-primary hover:underline">Autres comparatifs</Link>
              </div>
            </section>

            <footer className="text-xs text-muted-foreground pt-8 border-t border-border">
              <p>
                {brand.name} est une marque de son propriétaire. Page non affiliée ; comparaison impartiale.
              </p>
            </footer>
          </div>
        </div>
      </div>
    </>
  );
}